import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import useMobile from "../../hooks/useMobile";
import { Link } from "react-router-dom";
import ConfirmPopUp from "../ConfirmPopUp";
import DeletePopUp from "../DeletePopUp";
import { ICooperative } from "../../exports/interfaces";
import { useLoader } from "../../context/LoaderContext";
import { fetchCooperative } from "../../api/DashboardApi";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import axiosClient from "../../axioxClient.ts/axiosClient";
import { Eye, Pencil } from "lucide-react";
import { useOption } from "../../context/OptionContext";
import { darkenColor } from "../../exports/functions";
import { usePermission } from "../../hooks/usePermission";
import { useRole } from "../../hooks/useRole";

const CooperativeTable = ({
  cooperative,
  setCooperative,
}: {
  cooperative: ICooperative[];
  setCooperative: Dispatch<SetStateAction<ICooperative[]>>;
}) => {
  const { statusOptions } = useOption();
  const { isSuperAdmin, isAdmin } = useRole();

  const statusStyle = (status: string) => {
    const cooperativeStatuses =
      statusOptions?.find((item) => item.name === "Cooperative Statuses")
        ?.statusOptions || [];

    const matchedStatus = cooperativeStatuses.find(
      (item) => item.label === status,
    );

    return {
      backgroundColor: matchedStatus?.color || "#ccc",
      color: darkenColor(matchedStatus?.color || "#ccc", 40),
    };
  };

  const natureClass = (nature: string) => {
    switch (nature) {
      case "Multipurpose":
        return "pill-info";
      case "Saving and Credit":
        return "pill-danger";
      case "Agriculture":
        return "pill-success";
      case "Others":
        return "pill-primary";
      default:
        return "bg-secondary";
    }
  };
  const { setLoading } = useLoader();
  const { canEdit, canDelete } = usePermission();
  const isMobile = useMobile();
  const [showDelete, setShowDelete] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [openStatusId, setOpenStatusId] = useState<number | null>(null);
  const [selectedStatusId, setSelectedStatusId] = useState<number | null>(null);
  const [selectedStatusValue, setSelectedStatusValue] = useState<string>("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [statusDropDirection, setStatusDropDirection] = useState<"down" | "up">(
    "down",
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [showAll, setShowAll] = useState(false);
  const itemsPerPage = 25;

  const handleStatusBadgeClick = (
    id: number,
    e: React.MouseEvent<HTMLSpanElement, MouseEvent>,
  ) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const menuHeight = Math.min(cooperativeStatuses.length * 42 + 12, 260);
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    const direction = (() => {
      if (spaceBelow >= menuHeight) return "down";
      if (spaceAbove >= menuHeight) return "up";
      return spaceAbove > spaceBelow ? "up" : "down";
    })();

    setStatusDropDirection(direction);
    setOpenStatusId((prev) => (prev === id ? null : id));
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentData = showAll
    ? cooperative
    : cooperative.slice(indexOfFirstItem, indexOfLastItem);

  const totalPages = Math.ceil(cooperative.length / itemsPerPage);
  const cooperativeStatuses =
    statusOptions?.find((item) => item.name === "Cooperative Statuses")
      ?.statusOptions || [];

  const handleClose = () => {
    setShowDelete(false);
  };
  const handleDelete = async () => {
    setLoading(true);
    try {
      if (deleteId === null) return;

      const response = await axiosClient.delete(`/registers/${deleteId}`);
      if (response.status === 200 || response.status === 201) {
        fetchCooperative()
          .then((cooperativeData) => {
            setCooperative(cooperativeData);
            setShowDelete(false);
            setDeleteId(null);
            setTimeout(() => {
              setLoading(false);
              dynamicSuccess("Registered cooperative deleted successfully!");
            }, 1000);
          })
          .catch((error) => {
            const message =
              error?.response?.data?.message || "Something went wrong!";
            dynamicFailed(message);
            setTimeout(() => {
              setLoading(false);
            }, 1000);
          });
      }
    } catch (error: any) {
      const message = error?.response?.data?.message || "Something went wrong!";
      dynamicFailed(message);
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  const handleStatusSelect = (id: number, status: string) => {
    setSelectedStatusId(id);
    setSelectedStatusValue(status);
    setShowConfirm(true);
  };

  const handleConfirmStatusChange = async () => {
    if (selectedStatusId === null) return;

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("status", selectedStatusValue);

      const response = await axiosClient.put(
        `/registers/${selectedStatusId}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        },
      );

      if (response.status === 200 || response.status === 201) {
        setCooperative((prev) =>
          prev.map((item) =>
            item.id === selectedStatusId
              ? {
                  ...item,
                  status: selectedStatusValue,
                }
              : item,
          ),
        );
        dynamicSuccess("Status updated successfully!");
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message || "Failed to update status.";
      dynamicFailed(message);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setShowConfirm(false);
        setOpenStatusId(null);
        setSelectedStatusId(null);
        setSelectedStatusValue("");
      }, 1000);
    }
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenStatusId(null);
    if (openStatusId !== null) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [openStatusId]);

  return (
    <>
      <div id="cooperative-table" className="table-dashboard-card">
        <div className="table-responsive">
          <table className="table align-middle">
            <thead>
              <tr>
                {/* <th></th> */}
                <th>Reg no.</th>
                <th>Business no.</th>
                <th>Name</th>
                <th>Reg. PAN no.</th>
                <th>Email Address</th>
                <th>Mobile no.</th>
                <th>Status</th>
                <th>Nature of Work</th>
                <th className="sticky-action-header">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentData.map((item, i) => (
                <tr key={i}>
                  {/* <td>
                    <input type="checkbox" />
                  </td> */}

                  <td className="text-danger">{item.registrationNumber}</td>
                  <td className="text-danger">
                    {item.businessregistrationNumber}
                  </td>
                  <td className="table-bold">{item.nameEnglish}</td>
                  <td>{item.panNumber}</td>
                  <td>{item.email}</td>
                  <td>{item.mobileNumber}</td>

                  <td>
                    {isSuperAdmin || isAdmin ? (
                      <div
                        className="status-dropdown-container"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span
                          className="badge rounded-pill px-3 py-2 status-badge-clickable"
                          style={statusStyle(item.status)}
                          onClick={(e) => handleStatusBadgeClick(item.id, e)}
                        >
                          {item.status}
                        </span>

                        {openStatusId === item.id && (
                          <div
                            className={`status-dropdown-menu status-dropdown-${statusDropDirection}`}
                          >
                            {cooperativeStatuses.map((option) => (
                              <button
                                key={option.id}
                                type="button"
                                className="status-dropdown-item"
                                onClick={() =>
                                  handleStatusSelect(item.id, option.label)
                                }
                              >
                                {option.label}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <span
                        className="badge rounded-pill px-3 py-2"
                        style={statusStyle(item.status)}
                      >
                        {item.status}
                      </span>
                    )}
                  </td>

                  <td>
                    <span
                      className={`badge rounded-pill px-3 py-2 ${natureClass(item.natureOfWork)}`}
                    >
                      {item.natureOfWork}
                    </span>
                  </td>

                  <td className="sticky-action">
                    <div className="d-flex gap-2 align-items-center">
                      <Link to={`/cooperatives-list/${item.id}`}>
                        <i
                          className="cursor-pointer"
                          data-toggle="tooltip"
                          data-placement="top"
                          title="View"
                        >
                          <Eye />
                        </i>
                      </Link>
                      <Link
                        to={
                          canEdit("cooperatives")
                            ? `/register/edit/${item.id}`
                            : "#"
                        }
                        onClick={(e) => {
                          if (!canEdit("cooperatives")) {
                            e.preventDefault();
                          }
                        }}
                        style={{
                          pointerEvents: canEdit("cooperatives")
                            ? "auto"
                            : "none",
                          opacity: canEdit("cooperatives") ? 1 : 0.5,
                        }}
                      >
                        <i
                          data-toggle="tooltip"
                          data-placement="top"
                          title={
                            canEdit("cooperatives") ? "Edit" : "No permission"
                          }
                        >
                          <Pencil size={20} />
                        </i>
                      </Link>
                      <i
                        className={`fa-solid fa-trash text-danger ${
                          canDelete("cooperatives")
                            ? "cursor-pointer"
                            : "opacity-50"
                        }`}
                        data-toggle="tooltip"
                        data-placement="top"
                        title={
                          canDelete("cooperatives") ? "Delete" : "No permission"
                        }
                        style={{
                          pointerEvents: canDelete("cooperatives")
                            ? "auto"
                            : "none",
                        }}
                        onClick={() => {
                          if (!canDelete("cooperatives")) return;

                          setDeleteId(item.id);
                          setShowDelete(true);
                        }}
                      ></i>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div
          className={`d-flex justify-content-between align-items-center flex-wrap ${isMobile ? "gap-2" : ""} table-footer`}
        >
          <div className="d-flex align-items-center gap-3">
            <small>
              {showAll
                ? `All ${cooperative.length} Cooperatives`
                : `${indexOfFirstItem + 1}–${Math.min(indexOfLastItem, cooperative.length)} of ${cooperative.length} Cooperatives`}
            </small>
            {cooperative.length > itemsPerPage && (
              <h6
                className="mb-0 cursor-pointer"
                onClick={() => {
                  setShowAll((prev) => !prev);
                  setCurrentPage(1);
                }}
              >
                {showAll ? `Show 25 out of ${cooperative.length}` : "Show All"}
              </h6>
            )}
          </div>

          {!showAll && <div className="pagination">
            <button
              className="pagination-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              <i className="fa-solid fa-angle-left"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => (
              <button
                key={i}
                className={`pagination-page ${
                  currentPage === i + 1 ? "active" : ""
                }`}
                onClick={() => setCurrentPage(i + 1)}
              >
                {i + 1}
              </button>
            ))}
            <button
              className="pagination-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              <i className="fa-solid fa-angle-right"></i>
            </button>
          </div>}
        </div>
      </div>
      {showDelete && (
        <DeletePopUp onClose={handleClose} onConfirm={handleDelete} />
      )}
      {showConfirm && (
        <ConfirmPopUp
          onClose={() => setShowConfirm(false)}
          onConfirm={handleConfirmStatusChange}
        />
      )}
    </>
  );
};

export default CooperativeTable;
